import { defineMessages } from 'react-intl';

export const MIN_PASSWORD_LENGTH = 6;

export const FieldNames = Object.freeze({
  CURRENT_USERNAME: 'currentUsername',
  CURRENT_PASSWORD: 'currentPassword',
  USERNAME: 'username',
  PASSWORD: 'password',
  BIRTH_YEAR: 'birthYear',
  ZIP_CODE: 'zipCode',
  GENDER: 'gender',
} as const);

export const maxChars = {
  COMMENTS: 500,
};

export const REGISTER_PRIVACY_URL =
  'http://help.pandora.com/customer/portal/articles/1468760-information-about-privacy-on-pandora#2';

export const messages = defineMessages({
  accountSettings: {
    id: 't.accountSettingsLabel',
    defaultMessage: 'Account Settings',
  },
  accountInfo: {
    id: 't.accountInfoLabel',
    defaultMessage: 'Account Info',
  },
  username: {
    id: 't.usernameLabel',
    defaultMessage: 'Email',
  },
  password: {
    id: 't.passwordLabel',
    defaultMessage: 'Password',
  },
  birthYear: {
    id: 't.birthYearLabel',
    defaultMessage: 'Birth Year',
  },
  postalCode: {
    id: 't.postalCodeLabel',
    defaultMessage: 'Zip/Postal Code',
  },
  gender: {
    id: 't.genderLabel',
    defaultMessage: 'Gender',
  },
  FEMALE: {
    id: 't.FEMALE',
    defaultMessage: 'Female',
  },
  MALE: {
    id: 't.MALE',
    defaultMessage: 'Male',
  },
  NON_BINARY: {
    id: 't.NON_BINARY',
    defaultMessage: 'Non-binary',
  },
  NOT_LISTED: {
    id: 't.NOT_LISTED',
    defaultMessage: 'Not Listed',
  },
  whyPandora: {
    id: 't.whyPandora',
    defaultMessage: 'Why does Pandora ask for my birth year, postal code and gender?',
  },
  whyModalTitle: {
    id: 't.whyModalTitle',
    defaultMessage: 'Why does Pandora ask for my birth year, postal code and gender?',
  },
  whyModalBody: {
    id: 't.whyModalBody',
    defaultMessage:
      'We use your birth year, postal code and gender to personalize your experience on Pandora. We always keep your information anonymous.',
  },
  close: {
    id: 't.close',
    defaultMessage: 'Close',
  },
  readMore: {
    id: 't.readMore',
    defaultMessage: 'Read More',
  },
  privacy: {
    id: 't.privacy',
    defaultMessage: 'Profile Privacy',
  },
  public: {
    id: 't.public',
    defaultMessage: 'Public',
  },
  publicInstruction: {
    id: 't.publicInstruction',
    defaultMessage: 'Allow other listeners to view my profile',
  },
  private: {
    id: 't.private',
    defaultMessage: 'Private',
  },
  privateInstruction: {
    id: 't.privateInstruction',
    defaultMessage: 'Keep my profile hidden',
  },
  edit: {
    id: 't.edit',
    defaultMessage: 'Edit',
  },
  cancel: {
    id: 't.cancel',
    defaultMessage: 'Cancel',
  },
  submit: {
    id: 't.submitButton',
    defaultMessage: 'Submit',
  },
  save: {
    id: 't.save',
    defaultMessage: 'Save',
  },
  goBack: {
    id: 't.goBack',
    defaultMessage: 'Go Back',
  },
  continue: {
    id: 't.continue',
    defaultMessage: 'Continue',
  },
  notifications: {
    id: 't.Notifications',
    defaultMessage: 'Notifications',
  },
  fromPandora: {
    id: 't.fromPandora',
    defaultMessage: 'From Pandora',
  },
  fromPandoraInstruction: {
    id: 't.fromPandoraInstruction',
    defaultMessage:
      'Email me personalized updates about my stations, new music, local concerts and special offers',
  },
  fromFriends: {
    id: 't.fromFriends',
    defaultMessage: 'From Friends',
  },
  fromFriendsCommentInstruction: {
    id: 't.fromFriendsCommentInstruction',
    defaultMessage: 'Email me when someone comments on my profile or one of my stations',
  },
  fromFriendsFollowInstruction: {
    id: 't.fromFriendsFollowInstruction',
    defaultMessage: 'Email me when someone starts following me or one of my stations',
  },
  fromArtists: {
    id: 't.fromArtists',
    defaultMessage: 'From Artists',
  },
  fromArtistsInstruction: {
    id: 't.fromArtistsInstruction',
    defaultMessage: 'Email me announcements from my favorite artists',
  },
  on: {
    id: 't.on',
    defaultMessage: 'On',
  },
  off: {
    id: 't.off',
    defaultMessage: 'Off',
  },
  passwordModalTitle: {
    id: 't.passwordModalTitle',
    defaultMessage: 'Password Required',
  },
  passwordModalInstructions: {
    id: 't.passwordModalInstructions',
    defaultMessage: 'Please enter your password to process your request',
  },
  passwordModalNewPasswordInstructions: {
    id: 't.passwordModalNewPasswordInstructions',
    defaultMessage: 'Please enter your current password to process your request',
  },
  passwordModalLogOutAllDevices: {
    id: 't.passwordModalLogOutAllDevices',
    defaultMessage: 'Log out all other devices',
  },
  passwordModalInputPlaceholder: {
    id: 't.passwordModalInputPlaceholder',
    defaultMessage: 'Your Password',
  },
  passwordModalGeneralError: {
    id: 't.passwordModalGeneralError',
    defaultMessage: 'Please close and verify the information in the form.',
  },
  confirmCancellation: {
    id: 't.confirmCancellation',
    defaultMessage: 'Please enter your password to confirm your cancellation.',
  },
  passwordModalIncorrectPasswordError: {
    id: 't.passwordModalIncorrectPasswordError',
    defaultMessage: 'Invalid password, please try again',
  },
  updateAccountSuccess: {
    id: 't.updateAccountSuccess',
    defaultMessage: 'Changes Saved!',
  },
  switchOn: {
    id: 'switchOn',
    defaultMessage: 'On',
  },
  switchOff: {
    id: 'switchOff',
    defaultMessage: 'Off',
  },
  contentSettings: {
    id: 'contentSettings',
    defaultMessage: 'Content Settings',
  },
  explicitContent: {
    id: 'explicitContent',
    defaultMessage: 'Explicit Content',
  },
  allowExplicitContent: {
    id: 'allowExplicitContent',
    defaultMessage: 'Allow explicit content',
  },
  audioQuality: {
    id: 'highQualityAudio',
    defaultMessage: 'Audio Quality',
  },
  offerHighQualityAudio: {
    id: 'offerHighQualityAudio',
    defaultMessage: 'The best audio quality we offer',
  },
  passwordRequired: {
    id: 'passwordRequired',
    defaultMessage: '*Password Required',
  },
  artistAudioTitle: {
    id: 'artistAudioTitle',
    defaultMessage: 'Artist Audio Messages',
  },
  artistAudioInformation: {
    id: 't.artistAudioInformation',
    defaultMessage: 'Get audio messages from your favorite artists',
  },
  showStations: {
    id: 't.showStations',
    defaultMessage: 'Show Stations',
  },
  hideStations: {
    id: 't.hideStations',
    defaultMessage: 'Hide Stations',
  },
  pluralizedStations: {
    id: 't.pluralizedStations',
    defaultMessage:
      '{ numCheckedStations, number } of { numStations, plural, one {# station} other {# stations} }',
  },
  stationSingular: {
    id: 't.stationSingular',
    defaultMessage: 'station',
  },
  stationPlural: {
    id: 't.stationPlural',
    defaultMessage: 'stations',
  },
  social: {
    id: 'social',
    defaultMessage: 'Social',
  },
  twitter: {
    id: 'twitter',
    defaultMessage: 'Twitter',
  },
  facebook: {
    id: 'facebook',
    defaultMessage: 'Facebook',
  },
  connect: {
    id: 'connect',
    defaultMessage: 'Connect',
  },
  connected: {
    id: 'connected',
    defaultMessage: 'Connected',
  },
  connectedAs: {
    id: 'connectedAs',
    defaultMessage: 'Connected as {name}',
  },
  facebookInformation: {
    id: 'facebookInformation',
    defaultMessage: 'Import your profile picture, music likes, and friends list',
  },
  twitterInformation: {
    id: 'twitterInformation',
    defaultMessage: 'Connecting makes it easy for you to share on Twitter',
  },
  changeToMonthlyHeader: {
    id: 't.changeToMonthlyHeader',
    defaultMessage: 'Switch to monthly plan?',
  },
  changeToAnnualHeader: {
    id: 't.changeToAnnualHeader',
    defaultMessage: 'Switch to annual plan?',
  },
  changeToMonthly: {
    id: 't.changeToMonthly',
    defaultMessage:
      'When you confirm this switch, your monthly payment plan will start after your current annual billing cycle ends on {date}.  ',
  },
  changeToAnnual: {
    id: 't.changeToAnnual',
    defaultMessage:
      'When you confirm this switch, your annual payment plan will start after your current monthly billing cycle ends on {date}. ',
  },
  cancelHeader: {
    id: 't.cancelHeader',
    defaultMessage: 'Are you sure?',
  },
  cancelSubscrPremiumText: {
    id: 't.cancelSubscrPremiumText',
    defaultMessage:
      'If you cancel, you will lose access to your songs, albums, playlists, and downloads, as soon as your subscription expires.',
  },
  cancelSubscrText: {
    id: 't.cancelSubscrText',
    defaultMessage:
      'If you cancel, you’ll lose ad-free listening as well as more skips and fewer timeouts as soon as your subscription expires. ',
  },
  insufficientGiftCardBalanceForCancel: {
    id: 'insufficientGiftCardBalanceForCancel',
    defaultMessage:
      'Also, you only have {giftCardRemainingBalance} remaining on your gift card. ' +
      "If you cancel now the remaining value on your gift card will be lost and you won't be able to " +
      "use the remaining balance on this gift card because it isn't sufficient to pay for the least expensive plan. " +
      'See {pandoraHelpLink} for additional information.',
  },
  yesCancel: {
    id: 't.yesCancel',
    defaultMessage: 'Yes, cancel my subscription',
  },
  dismiss: {
    id: 't.dismiss',
    defaultMessage: 'Dismiss',
  },
  passwordMustBeLength: {
    id: 't.passwordMustBeLength',
    defaultMessage: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
  },
  ZIP_CODE_INVALID: {
    id: 't.ZIP_CODE_INVALID',
    defaultMessage: 'Invalid ZIP code.',
    field: 'zipcode',
  },
  POSTAL_CODE_INVALID: {
    id: 't.POSTAL_CODE_INVALID',
    defaultMessage: 'Invalid postal code.',
    field: 'zipcode',
  },
  BIRTH_YEAR_INVALID: {
    id: 't.BIRTH_YEAR_INVALID',
    defaultMessage: 'Please enter a valid birth year.',
    field: 'birthYear',
  },
  BIRTH_YEAR_TOO_YOUNG: {
    id: 't.BIRTH_YEAR_TOO_YOUNG',
    defaultMessage: 'We cannot save your information at this time.',
    field: 'birthYear',
  },
  confirmNavigationTitle: {
    id: 't.confirmNavigationTitle',
    defaultMessage: 'Are you sure?',
  },
  confirmNavigationBody: {
    id: 't.confirmNavigationBody',
    defaultMessage:
      'You’ve made some changes that haven’t been saved. Do you want to leave this page without saving them?',
  },
  buttonCancel: {
    id: 't.buttonCancel',
    defaultMessage: 'Cancel',
  },
  buttonLeavePage: {
    id: 't.buttonOk',
    defaultMessage: 'Leave Page',
  },
  changesNotSaved: {
    id: 't.changesNotSaved',
    defaultMessage: 'Changes not saved.',
  },
  // UPDATED SUBSCRIPTION MODAL MESSAGES
  youAreSubscribingTo: {
    id: 't.youAreSubscribingTo',
    defaultMessage: 'You are subscribing to: ',
  },
  familyConfirmLeavingFamilyPlan: {
    id: 't.familyConfirmLeavingFamilyPlan',
    defaultMessage: 'Yes, Leave Family Plan',
  },
  familyConfirmRemovingFamilyPlanChild: {
    id: 't.familyConfirmRemovingFamilyPlanChild',
    defaultMessage: 'Yes, Remove From Plan',
  },
  LISTENER_DUPLICATE_USERNAME: {
    id: 't.LISTENER_DUPLICATE_USERNAME',
    defaultMessage: 'An account already exists with that email address.',
  },
  logOutAllDevicesLink: {
    id: 't.logOutAllDevicesLink',
    defaultMessage: 'Log out of all other devices',
  },
  titleLogOutAllDevices: {
    id: 't.titleLogOutAllDevices',
    defaultMessage: 'Log out of all other devices',
  },
  contentLogOutAllDevices: {
    id: 't.contentLogOutAllDevices',
    defaultMessage: 'To process your request, please enter your password below.',
  },
  noteLogOutAllDevices: {
    id: 't.noteLogOutAllDevices',
    defaultMessage: 'Note: It may take up to four (4) hours to log out of all other devices.',
  },
  buttonLogOutAllDevices: {
    id: 't.buttonLogOutAllDevices',
    defaultMessage: 'Log Out',
  },
  buttonCancelLogOutAllDevices: {
    id: 't.buttonCancelLogOutAllDevices',
    defaultMessage: 'Cancel',
  },
  titleLogOutAllDevicesSuccess: {
    id: 't.titleLogOutAllDevicesSuccess',
    defaultMessage: 'Your request has been processed',
  },
  deleteAccountLink: {
    id: 't.deleteAccountLink',
    defaultMessage: 'Delete Pandora account',
  },
  titleAreYouSure: {
    id: 't.titleAreYouSure',
    defaultMessage: 'Are you sure?',
  },
  cancelSubscription: {
    id: 't.cancelSubscription',
    defaultMessage: 'Cancel your subscription',
  },
  contentAreYouSure: {
    id: 't.contentAreYouSure',
    defaultMessage:
      'You’ll lose access to your music, Thumbs and the Pandora Community. To cancel your Community account, please contact support.',
  },
  buttonDeleteAccount: {
    id: 't.buttonDeleteAccount',
    defaultMessage: 'Yes, Delete Account',
  },
  buttonKeepAccount: {
    id: 't.buttonKeepAccount',
    defaultMessage: 'Cancel',
  },
  titleEnterPassword: {
    id: 't.titleEnterPassword',
    defaultMessage: 'Password Required',
  },
  contentEnterPassword: {
    id: 't.contentEnterPassword',
    defaultMessage:
      'Enter your password to delete your account and your stations on Pandora. This cannot be undone.',
  },
  titleEnterEmail: {
    id: 't.titleEnterEmail',
    defaultMessage: 'Please enter contact email',
  },
  buttonSubmit: {
    id: 't.buttonSubmit',
    defaultMessage: 'Submit',
  },
  titleEmailSubmitted: {
    id: 't.titleEmailSubmitted',
    defaultMessage: 'Email submitted',
  },
  contentEmailSubmitted: {
    id: 't.contentEmailSubmitted',
    defaultMessage:
      'One of our listener advocates will send you an email at {email} with instructions for how to cancel your Pandora subscription and delete your account.',
  },
  enterAnEmail: {
    id: 't.enterAnEmail',
    defaultMessage: 'Please enter an email address.',
  },
  invalidEmail: {
    id: 't.invalidEmail',
    defaultMessage: 'Invalid email',
  },
  incorrectPasswordError: {
    id: 't.incorrectPasswordError',
    defaultMessage: 'Invalid password, please try again.',
  },
  titleAccountDeleted: {
    id: 't.titleAccountDeleted',
    defaultMessage: 'Your Pandora account has been deleted',
  },
  ok: {
    id: 't.ok',
    defaultMessage: 'Ok',
  },
  downloadApp: {
    id: 't.downloadapp',
    defaultMessage: 'Download App',
  },
  findPandoraTagLine: {
    id: 't.findPandoraTagLine',
    defaultMessage: 'Find Pandora across multiple devices to keep the music going.',
  },
  downloadDesktopApp: {
    id: 't.downloadDesktopApp',
    defaultMessage: 'Download the Desktop app',
  },
} as const);
